import dayjs from "dayjs"

import {
  calculateLineTotal,
  convertFromTon,
  convertPreviewQuantity,
  convertToTon,
  getTonUomId,
  isProductInTon,
  isTonUom,
  isUsUom,
} from "./conversion"

import {
  type AerosDateValue,
  DateSpecialValue,
} from "@/components/UiKit/AerosDatePicker/AerosDatePickerWithCustomActions"
import { DATE_FORMAT, UOM_RENDER_PRECISION } from "@/constants/formats"
import {
  CompanyDefaultUnitOfMeasurement,
  type OrderLine,
  type OrderLineInput,
  type Product,
  type PurchaseOrderPricingInput,
  type PurchaseOrderPricingLineResult,
} from "@/graphql/codegen/graphql"
import { getLocationIdPerProductList } from "@/screens/Procurement/PurchaseOrder/hooks/utils/common"
import {
  type PricingOrderLine,
  RoundOffType,
  RoundPrecisionUnit,
} from "@/screens/Procurement/PurchaseOrder/ProductAdded/types/pricing"
import { type PurchaseOrderForm } from "@/screens/Procurement/PurchaseOrder/types"
import { type ProductSelectionItem } from "@/screens/Procurement/Supplier/types/types"
import { type UomAmountWithId } from "@/types/convertion"

interface POTotalSummary {
  totalPallets: number
  totalWeight: {
    us: UomAmountWithId
    metric: UomAmountWithId
  }
  totalAmount: number
}

interface ConvertedQuantity {
  amount: string | number
  uomId: string
}

interface LineWeights {
  usWeight: UomAmountWithId
  metricWeight: UomAmountWithId
}

const processUsUomWeights = (
  quantityInUom: number,
  uomId: string,
  convertedQuantity: ConvertedQuantity
): LineWeights => {
  const amount = parseFloat(convertedQuantity.amount.toString())

  let usWeight: UomAmountWithId
  if (isTonUom(uomId)) {
    usWeight = {
      uomId: CompanyDefaultUnitOfMeasurement.UsstdWeightPound,
      amount: convertFromTon(quantityInUom, uomId),
    }
  } else {
    usWeight = {
      uomId,
      amount: quantityInUom,
    }
  }

  let metricWeight: UomAmountWithId
  if (isTonUom(convertedQuantity.uomId)) {
    metricWeight = {
      uomId: CompanyDefaultUnitOfMeasurement.MetricWeightKilogram,
      amount: convertFromTon(amount, convertedQuantity.uomId),
    }
  } else {
    metricWeight = {
      uomId: convertedQuantity.uomId,
      amount,
    }
  }

  return { usWeight, metricWeight }
}

const processMetricUomWeights = (
  quantityInUom: number,
  uomId: string,
  convertedQuantity: ConvertedQuantity
): LineWeights => {
  const amount = parseFloat(convertedQuantity.amount.toString())

  let usWeight: UomAmountWithId
  if (isTonUom(convertedQuantity.uomId)) {
    usWeight = {
      uomId: CompanyDefaultUnitOfMeasurement.UsstdWeightPound,
      amount: convertFromTon(amount, convertedQuantity.uomId),
    }
  } else {
    usWeight = {
      uomId: convertedQuantity.uomId,
      amount,
    }
  }

  let metricWeight: UomAmountWithId
  if (isTonUom(uomId)) {
    metricWeight = {
      uomId: CompanyDefaultUnitOfMeasurement.MetricWeightKilogram,
      amount: convertFromTon(quantityInUom, uomId),
    }
  } else {
    metricWeight = {
      uomId,
      amount: quantityInUom,
    }
  }

  return { usWeight, metricWeight }
}

export const calculateWeightTotals = (quantityInUom: number, uomId: string, product: Product): LineWeights => {
  const convertedQuantity = convertPreviewQuantity({ quantityInUom, uomId, product })

  if (isUsUom(uomId)) {
    return processUsUomWeights(quantityInUom, uomId, convertedQuantity)
  } else {
    return processMetricUomWeights(quantityInUom, uomId, convertedQuantity)
  }
}

interface GetTotalSummaryPurchaseOrderParams {
  orderLines?: OrderLineInput[] | OrderLine[]
  requestedDeliveryDate?: string
  productsCalculatedPricing?: PurchaseOrderPricingLineResult[]
}

// TODO: remove this method in scope of https://robotsandpencils.atlassian.net/browse/AK-5154
export const getTotalSummaryPurchaseOrder = ({
  orderLines,
  productsCalculatedPricing,
}: GetTotalSummaryPurchaseOrderParams): POTotalSummary => {
  const { totalPallets, totalWeight, totalAmount } = (orderLines ?? []).reduce<POTotalSummary>(
    (acc, line) => {
      const { numberOfPallets = 0, quantityInUom = 0, uomId, product } = line as OrderLine & { product: Product }

      acc.totalPallets += numberOfPallets ?? 0

      const productPricing = productsCalculatedPricing?.find((p) => p.productId === line.productId)

      const currentPrice = line.pricePerUom ?? 0

      const pricingUomId = line.isManualPriceSet
        ? line.priceUomId
        : (productPricing?.pricingPackage?.unitOfMeasurementId ?? line.priceUomId)

      const lineTotal = calculateLineTotal({
        currentPrice,
        pricingUomId,
        product,
        quantity: quantityInUom,
        uomId,
      })

      acc.totalAmount += lineTotal

      const lineWeights = calculateWeightTotals(quantityInUom, uomId, product)

      acc.totalWeight.us.uomId = lineWeights.usWeight.uomId
      acc.totalWeight.us.amount += lineWeights.usWeight.amount

      acc.totalWeight.metric.uomId = lineWeights.metricWeight.uomId
      acc.totalWeight.metric.amount += lineWeights.metricWeight.amount

      return acc
    },
    {
      totalPallets: 0,
      totalWeight: { us: { amount: 0, uomId: "" }, metric: { amount: 0, uomId: "" } },
      totalAmount: 0,
    }
  )

  return {
    totalPallets: parseFloat(totalPallets.toFixed(UOM_RENDER_PRECISION)),
    totalWeight: {
      us: {
        amount: totalWeight.us.amount,
        uomId: totalWeight.us.uomId || "-",
      },
      metric: {
        amount: totalWeight.metric.amount,
        uomId: totalWeight.metric.uomId || "-",
      },
    },
    totalAmount,
  }
}

export const getWeightPerPallet = (product: Product, uomId: string): number => {
  const { palletMetricWeight, palletUsWeight } = product

  const weightPerPallet = isUsUom(uomId) ? palletUsWeight : palletMetricWeight

  return weightPerPallet ?? 0
}

interface RoundingValues {
  lower: number
  upper: number
}

export const getRoundedPallets = (value: number): RoundingValues => {
  if (value % 1 === 0) {
    return { lower: value, upper: value } // Return the same value for whole numbers
  }

  const lower = Math.floor(value)
  const upper = Math.ceil(value)

  return { lower, upper }
}

export const getRoundedBags = ({
  palletsNumber,
  weightPerPallet,
  packageUsWeight,
}: {
  palletsNumber: number
  weightPerPallet: number
  packageUsWeight: number
}): RoundingValues => {
  const palettesTotalWeight = palletsNumber * weightPerPallet
  const minFullPalettesWeight = palettesTotalWeight - (palettesTotalWeight % packageUsWeight)

  const lower = minFullPalettesWeight / weightPerPallet
  const upper = (minFullPalettesWeight + packageUsWeight) / weightPerPallet

  return { lower, upper }
}

const getRoundedValues = ({
  product,
  uomId,
  roundPrecisionUnit,
  palletsNumber,
}: {
  product: Product
  uomId: string
  roundPrecisionUnit: RoundPrecisionUnit
  palletsNumber: number
}): {
  lower: number
  upper: number
} => {
  const packageUsWeight = product.packageUsWeight ?? 0
  const weightPerPallet = getWeightPerPallet(product, uomId)

  if (roundPrecisionUnit === RoundPrecisionUnit.Bag) {
    const palettesTotalWeight = palletsNumber * weightPerPallet
    const minFullPalettesWeight = palettesTotalWeight - (palettesTotalWeight % packageUsWeight)

    const lower = minFullPalettesWeight / weightPerPallet
    const upper = (minFullPalettesWeight + packageUsWeight) / weightPerPallet

    return { lower, upper }
  }

  if (roundPrecisionUnit === RoundPrecisionUnit.Pallet) {
    if (palletsNumber % 1 === 0) {
      return { lower: palletsNumber, upper: palletsNumber }
    }

    const lower = Math.floor(palletsNumber)
    const upper = Math.ceil(palletsNumber)

    return { lower, upper }
  }

  return { lower: 0, upper: 0 }
}

interface CalculateRoundingValuesParams {
  palletsNumber: number
  quantityInUom: number
  uomId: string
  product: Product
  roundPrecisionUnit: RoundPrecisionUnit
}

export const calculateRoundingValues = ({
  palletsNumber,
  quantityInUom,
  uomId,
  product,
  roundPrecisionUnit,
}: CalculateRoundingValuesParams): {
  lowerWeight: number
  upperWeight: number
  lowerNumber: number
  upperNumber: number
} => {
  const weightPerPallet = getWeightPerPallet(product, uomId)

  const { lower: lowerNumber, upper: upperNumber } = getRoundedValues({
    product,
    uomId,
    roundPrecisionUnit,
    palletsNumber,
  })

  let lowerWeight = weightPerPallet ? lowerNumber * weightPerPallet : quantityInUom
  let upperWeight = weightPerPallet ? upperNumber * weightPerPallet : quantityInUom

  if (isTonUom(uomId) && !isProductInTon(product)) {
    lowerWeight = convertToTon(lowerWeight, uomId)
    upperWeight = convertToTon(upperWeight, uomId)
  } else if (!isTonUom(uomId) && isProductInTon(product)) {
    const tonUomId = getTonUomId(uomId)

    lowerWeight = convertFromTon(lowerWeight, tonUomId)
    upperWeight = convertFromTon(upperWeight, tonUomId)
  }

  return {
    lowerWeight,
    upperWeight,
    lowerNumber,
    upperNumber,
  }
}

interface RoundOrderQuantityParams {
  line: PricingOrderLine
  roundOffType: RoundOffType | null
  upper: number
  upperPallets: number
  lower: number
  lowerPallets: number
}

export const hasFloatPallets = (numberOfPallets: number): boolean => (numberOfPallets ?? 0) % 1 !== 0

export const roundOrderQuantity = ({
  line,
  roundOffType,
  upper,
  upperPallets,
  lower,
  lowerPallets,
}: RoundOrderQuantityParams): PricingOrderLine => {
  if (roundOffType === RoundOffType.UP) {
    return {
      ...line,
      quantityInUom: upper,
      numberOfPallets: upperPallets,
      hasFloatPallets: hasFloatPallets(upperPallets),
    }
  }

  if (roundOffType === RoundOffType.DOWN) {
    return {
      ...line,
      quantityInUom: lower,
      numberOfPallets: lowerPallets,
      hasFloatPallets: hasFloatPallets(lowerPallets),
    }
  }

  return {
    ...line,
    hasFloatPallets: false,
  }
}

export const isBagsWholeNumber = (product: Product, quantityInUom: number, uomId: string): boolean => {
  const weight = isUsUom(uomId) ? product.packageUsWeight : product.packageMetricWeight

  if (!weight) {
    return false
  }

  return quantityInUom % weight === 0
}

export const parseNumberOfPallets = (numberOfPallets: number, isUserInput: boolean): string | number => {
  if (isUserInput && !numberOfPallets) {
    return ""
  }

  if (numberOfPallets) {
    return Number.parseFloat(numberOfPallets.toFixed(UOM_RENDER_PRECISION))
  }

  return 0
}

const createNextDay = () => dayjs().add(1, "day").format(DATE_FORMAT.ISO_18601)

// On PO create/update date must be sent as null when ASAP is selected.
// But in order to fetch actual pricing while PO is being edited, next day date is provided.
export const resolveSpecialDate = (
  date: AerosDateValue | undefined,
  sentinel: DateSpecialValue,
  preserveDate = false
): string | null => {
  if (date && date !== sentinel) {
    return date
  }
  return preserveDate && sentinel === DateSpecialValue.ASAP ? createNextDay() : null
}

export const mapCalculatedPricingParams = ({
  orderForm,
  product,
  quantity,
  requestedDeliveryDate,
  updatedUomId,
}: {
  orderForm: PurchaseOrderForm
  requestedDeliveryDate?: AerosDateValue
  updatedUomId?: string
  product: Product | null
  quantity: number | null
}): PurchaseOrderPricingInput => {
  const sourceCompanyLocationAssociationId = getLocationIdPerProductList(
    orderForm.orderLines.map(({ product }) => product)
  )

  const date = requestedDeliveryDate !== undefined ? requestedDeliveryDate : orderForm.requestedDeliveryDate

  return {
    orderLines: orderForm.orderLines.map(
      ({ productId, quantityInUom, uomId, productVersionId, isManualPriceSet, pricePerUom, priceUomId }) => ({
        productId,
        productVersionId,
        quantity: Math.round(productId === product?.productId && quantity !== null ? quantity : quantityInUom),
        uomId: productId === product?.productId && !!updatedUomId ? updatedUomId : uomId,
        isManualPriceSet,
        pricePerUom: isManualPriceSet ? pricePerUom : undefined,
        priceUomId: isManualPriceSet ? priceUomId : undefined,
      })
    ),
    requestedDeliveryDate: resolveSpecialDate(date, DateSpecialValue.ASAP, true),
    sourceCompanyLocationAssociationId,
  }
}

export const mapProductsPricingParams = (products: ProductSelectionItem[]): PurchaseOrderPricingInput => ({
  orderLines: products.map(({ productId, versionId }) => ({
    productId,
    productVersionId: versionId as number,
    quantity: 0,
    uomId: "",
  })),
  requestedDeliveryDate: createNextDay(),
  sourceCompanyLocationAssociationId: getLocationIdPerProductList(products),
})
